/**
 * What the guided first visit points at. Each is a testID on the element (rendered as data-testid on web), so the
 * tour finds and watches them without changing how they behave.
 */
export const TARGET = {
  lessonsButton: 'hud-lessons',
  collectionStore: 'hud-collection-store',
  upgrade: 'hud-upgrade',
  exit: 'hud-exit',
  storeClose: 'collection-store-close',
  plansClose: 'plans-close',
  tileTutor: 'control-tutor',
  tileFly: 'control-fly',
  flyUp: 'control-fly-up',
  flyDown: 'control-fly-down',
  controlsHandle: 'controls-expand',
  tutorModeChoice: 'tutor-mode-choice',
  tutorModeVoice: 'tutor-mode-voice',
  lessonMenuHeader: 'lesson-menu-header',
  unitMenuHeader: 'unit-menu-header',
};

/** Teleport on a unit card in the course portal's unit menu. */
export const unitTeleportTarget = unitNumber => `unit-teleport-${unitNumber}`;

/** The lesson menu's Start Lesson / Start Quiz buttons for one lesson. */
export const startLessonTarget = lessonId => `lesson-menu-start-lesson-${lessonId}`;
export const startQuizTarget = lessonId => `lesson-menu-start-quiz-${lessonId}`;

/**
 * How far something at the centre of the window travels to reach the Collection Store button (or straight up when the
 * button is not on screen) - where badges and coins fly to.
 */
export function flightToStore() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return { x: 0, y: -300 };
  const store = findTarget(TARGET.collectionStore);
  if (!store) return { x: 0, y: -window.innerHeight / 2 };
  const rect = store.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2 - window.innerWidth / 2,
    y: rect.top + rect.height / 2 - window.innerHeight / 2,
  };
}

/** The element with this test id, or null (also off the web). */
export function findTarget(testId) {
  if (!testId || typeof document === 'undefined') return null;
  return document.querySelector(`[data-testid="${testId}"]`);
}

/** The test id of the nearest marked element at or above `element`, or ''. */
export function testIdOf(element) {
  const marked = element && typeof element.closest === 'function' ? element.closest('[data-testid]') : null;
  return marked ? marked.getAttribute('data-testid') || '' : '';
}

/**
 * Whether a measured box is on screen. A button in the collapsed controls panel keeps its size but sits off the
 * right edge (measured on DEV 2026-10-10: FLY at x=1506 in a 1440-wide window), so size alone is not enough.
 */
export function isOnScreen(box, viewWidth, viewHeight) {
  return Boolean(
    box && box.width > 0 && box.height > 0 &&
    box.left < viewWidth && box.left + box.width > 0 && box.top < viewHeight && box.top + box.height > 0,
  );
}
